import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { supabase } from '@/integrations/supabase/client';

// Forced-update gate (P0-DIST-01 / SEC-0.4). Runs before the mobile shell mounts. Web is never gated.
// Reads public.app_min_version (RLS: authenticated SELECT). Fail-OPEN on any error so a transient
// network/RLS issue never bricks the app.
//
// NOTE: app_min_version is a new table not yet in the generated Supabase types — the `as never` cast
// is a deliberate, temporary escape hatch. Regenerate src/integrations/supabase/types.ts (Supabase MCP
// generate_typescript_types) to remove it.
export interface VersionGateResult {
  blocked: boolean;
  message?: string;
  currentBuild?: number;
  minBuild?: number;
}

export async function checkMinVersion(): Promise<VersionGateResult> {
  if (!Capacitor.isNativePlatform()) return { blocked: false };
  try {
    const platform = Capacitor.getPlatform(); // 'ios' | 'android'
    const info = await App.getInfo();
    const build = parseInt(info.build, 10);

    const { data, error } = await supabase
      .from('app_min_version' as never)
      .select('min_build, message')
      .eq('platform', platform)
      .maybeSingle();

    const row = data as { min_build: number; message: string | null } | null;
    if (error || !row) return { blocked: false };

    if (Number.isFinite(build) && build < row.min_build) {
      return {
        blocked: true,
        message: row.message ?? 'A newer version of the CBAM app is required.',
        currentBuild: build,
        minBuild: row.min_build,
      };
    }
    return { blocked: false, currentBuild: build, minBuild: row.min_build };
  } catch {
    return { blocked: false };
  }
}
